import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  Button,
  Input,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { useToast } from '@vnetwork/ui/feedback';
import { useCreatePrivateNetwork, usePrivateNetworkIncremental } from '@/api/opennebula';

/** Validate a CIDR string is a private RFC 1918 range. */
export function validatePrivateCidr(value: string): { valid: boolean; error?: string } {
  const trimmed = value.trim();
  const match = trimmed.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/);
  if (!match) return { valid: false, error: 'Invalid CIDR format (e.g. 10.0.1.0/24)' };

  const [, o1, o2, o3, o4, prefix] = match.map(Number);
  if ([o1, o2, o3, o4].some((octet) => octet > 255)) {
    return { valid: false, error: 'IP octets must be 0-255' };
  }
  if (prefix > 32) return { valid: false, error: 'Prefix must be 0-32' };

  const isPrivate =
    o1 === 10 || // 10.0.0.0/8
    (o1 === 172 && o2 >= 16 && o2 <= 31) || // 172.16.0.0/12
    (o1 === 192 && o2 === 168); // 192.168.0.0/16
  if (!isPrivate) {
    return { valid: false, error: 'Must be a private IP (10.x.x.x, 172.16-31.x.x, or 192.168.x.x)' };
  }
  return { valid: true };
}

interface CreateNetworkDialogProps {
  serviceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new network id once creation succeeds. */
  onCreated?: (networkId: string) => void;
}

/**
 * Create a private/reserved network from a custom RFC 1918 subnet CIDR. On
 * success the network list query is invalidated by the hook so the new network
 * appears in the attach picker.
 */
export function CreateNetworkDialog({ serviceId, open, onOpenChange, onCreated }: CreateNetworkDialogProps) {
  const { show } = useToast();
  const [cidr, setCidr] = useState('');
  const [touched, setTouched] = useState(false);

  // Pre-fill the CIDR field with the next free private subnet suggested by the API.
  const { data: suggested } = usePrivateNetworkIncremental(open ? serviceId : undefined);
  useEffect(() => {
    if (open && !touched && !cidr && suggested?.network_incremental) {
      setCidr(suggested.network_incremental);
    }
  }, [open, touched, cidr, suggested?.network_incremental]);

  const create = useCreatePrivateNetwork(serviceId, {
    onSuccess: (data) => {
      const created = (data as { data?: { network?: { id?: string; name?: string } } })?.data?.network;
      show({ tone: 'success', title: 'Network created', description: created?.name });
      if (created?.id) onCreated?.(created.id);
      handleOpenChange(false);
    },
    onError: (e) => show({ tone: 'danger', title: (e as Error)?.message || 'Could not create network' }),
  });

  const validation = useMemo(() => {
    if (!cidr.trim()) return { valid: false, error: undefined as string | undefined };
    return validatePrivateCidr(cidr);
  }, [cidr]);

  function reset() {
    setCidr('');
    setTouched(false);
  }

  function handleOpenChange(next: boolean) {
    if (!next) reset();
    onOpenChange(next);
  }

  function handleSubmit() {
    setTouched(true);
    const result = validatePrivateCidr(cidr);
    if (!result.valid) return;
    create.mutate({ cidr: cidr.trim() });
  }

  const showError = touched && !!validation.error;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Create network</DialogTitle>
          <DialogDescription>
            Set up a private network with a custom subnet. The first IP address (.1) is reserved as the gateway.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <form
            id="create-network-form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
            style={{ display: 'grid', gap: 'var(--vn-space-4)' }}
          >
            <Field
              label="Subnet (CIDR)"
              required
              description="Gateway IP (.1) is reserved automatically for the router."
              error={showError ? validation.error : undefined}
            >
              {(props) => (
                <Input
                  {...props}
                  autoFocus
                  placeholder="e.g. 10.0.1.0/24"
                  value={cidr}
                  invalid={showError}
                  onChange={(e) => setCidr(e.target.value)}
                  onBlur={() => setTouched(true)}
                />
              )}
            </Field>
            <Alert tone="info">
              A NAT IP address is assigned automatically, allowing connected instances to access the internet.
            </Alert>
          </form>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-network-form"
            disabled={!validation.valid || create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create network'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
